import React, { useCallback, useEffect } from 'react'
import { Row, Col } from 'antd'
import { SaveFilled } from '@ant-design/icons'
import { useOutletContext } from 'react-router-dom'
import SettingsChangeModal from '../../../Components/Settings/SettingsChangeModal'
import { TranslationKeys } from '../../../Constants/TranslationKeys'
import {
  handleModalTriggerEvent,
  isProdSite,
  renderTranslation,
  stopPropagation,
} from '../../../Helpers/GenericHelpers'
import { toggleChangeModal } from '../../../Slices/Settings/SettingsSlice'
import { updateChannels } from '../../../Services/Omnichannel/ChannelService'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import Channels from './Channels'
import Sites from './Sites'
import { savePricelists, updateEnableStatus } from 'Services/Settings/SiteSettingsService'
import { setChanges as setSiteChanges } from 'Slices/Settings/SiteSlice'
import { getPricelists } from 'Services/PricelistService'
import { resetChannelChanges } from 'Slices/Settings/ChannelSlice'
import Stores from './Stores'

function OmnichannelSettings(props) {
  const dispatch = useAppDispatch()
  const language = useAppSelector(state => state.present.language.value)
  const channelDataChanges = useAppSelector(state => state.present.channel.channelDataChanges)
  const siteDataChanges = useAppSelector(state => state.present.site.siteDataChanges)
  const sites = useAppSelector(state => state.present.configuration.sites)
  const omnichannelModule = useAppSelector(state => state.present.configuration.omnichannelModule)
  const omnichannelSiteModule = useAppSelector(state => state.present.configuration.omnichannelSiteModule)
  const omnichannelStoreModule = useAppSelector(state => state.present.configuration.omnichannelStoreModule)

  const bodyrefContext = useOutletContext() as React.MutableRefObject<undefined>

  const saveChanges = useCallback(() => {
    if (channelDataChanges.length > 0) {
      dispatch(updateChannels())
    }

    if (siteDataChanges && sites) {
      dispatch(savePricelists())
      dispatch(updateEnableStatus())

      dispatch(setSiteChanges(false))
    }
    dispatch(getPricelists())
  }, [channelDataChanges.length, siteDataChanges, sites, dispatch])

  const handleClickOutside = useCallback(
    event => {
      const callback = () => {
        event.preventDefault()
        stopPropagation(event)
        dispatch(toggleChangeModal(true))
      }
      const hasChanges = channelDataChanges.length > 0 || siteDataChanges

      const extraRef = document.querySelector('.collapse-container')

      handleModalTriggerEvent(event, bodyrefContext?.current, hasChanges, callback, null, extraRef)
    },
    [bodyrefContext, channelDataChanges.length, dispatch, siteDataChanges],
  )

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true)
    return () => {
      document.removeEventListener('click', handleClickOutside, true)
    }
  }, [handleClickOutside])

  return (
    <div className='w-100'>
      <Row>
        <Col span={24} className='channel-setting-upper-row'>
          <div className='title-box'>
            <div>{renderTranslation(language, TranslationKeys.titleChannelsSetting)}</div>
            <SaveFilled
              className={
                'generic-icon save-icon channel-save-icon ant-col' +
                (channelDataChanges.length > 0 || siteDataChanges ? ' active' : '')
              }
              onClick={saveChanges}
            />
          </div>
        </Col>
      </Row>
      <Col span={24} className='channel-tables-box'>
        <SettingsChangeModal
          onChangeConfirm={saveChanges}
          onChangeCancel={() => {
            dispatch(resetChannelChanges())
            dispatch(setSiteChanges(false))
          }}
          bodyref={bodyrefContext}
        />

        <Col span={!isProdSite() ? 24 : 8} className='channel-setting-table-box'>
          {omnichannelModule ? <Channels bodyref={bodyrefContext} /> : null}
          {!isProdSite() && omnichannelSiteModule ? <Sites /> : null}
          {!isProdSite() && omnichannelStoreModule ? <Stores /> : null}
        </Col>
      </Col>
    </div>
  )
}

export default OmnichannelSettings
